import { Application, Container, Sprite, Texture, CanvasSource } from 'pixi.js';
import { WA_PHYSICS } from '../core/constants';

declare global {
  interface Window {
    PIXI: typeof import('pixi.js');
  }
}

export async function createPixiApp(
  container: HTMLElement,
  _width: number = WA_PHYSICS.WORLD.WIDTH,
  _height: number = WA_PHYSICS.WORLD.HEIGHT,
): Promise<Application> {
  const app = new Application();

  const containerWidth = container.clientWidth || window.innerWidth;
  const containerHeight = container.clientHeight || window.innerHeight;

  await app.init({
    width: containerWidth,
    height: containerHeight,
    background: '#87ceeb',
    resolution: window.devicePixelRatio || 1,
    autoDensity: true,
    antialias: false,
    resizeTo: container,
  });

  const canvas = app.canvas as HTMLCanvasElement;
  canvas.style.display = 'block';
  canvas.style.width = '100%';
  canvas.style.height = '100%';

  canvas.addEventListener('webglcontextlost', (e) => {
    e.preventDefault();
    console.error('WebGL context lost');
  });

  container.appendChild(canvas);

  window.PIXI = await import('pixi.js');

  return app;
}

export function createLayer(name: string): Container {
  const layer = new Container();
  layer.label = name;
  return layer;
}

export function createSpriteFromDataURL(dataUrl: string): Sprite {
  const texture = Texture.from(dataUrl);
  return new Sprite(texture);
}

export function createSpriteFromCanvas(canvas: HTMLCanvasElement): Sprite {
  const source = new CanvasSource({ resource: canvas });
  const texture = new Texture({ source });
  return new Sprite(texture);
}

export interface RenderLayers {
  background: Container;
  terrain: Container;
  entities: Container;
  projectiles: Container;
  effects: Container;
  ui: Container;
}

export function createRenderLayers(stage: Container): RenderLayers {
  const layers: RenderLayers = {
    background: createLayer('background'),
    terrain: createLayer('terrain'),
    entities: createLayer('entities'),
    projectiles: createLayer('projectiles'),
    effects: createLayer('effects'),
    ui: createLayer('ui'),
  };

  stage.addChild(layers.background);
  stage.addChild(layers.terrain);
  stage.addChild(layers.entities);
  stage.addChild(layers.projectiles);
  stage.addChild(layers.effects);
  stage.addChild(layers.ui);

  return layers;
}
